/**
 * @fileoverview Cart API Service
 * Handles communication with Shopify Cart API
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS DISPATCHED:
 * - api:request_started  → Emitted when API request begins
 * - api:request_completed → Emitted when API request succeeds
 * - api:request_failed   → Emitted when API request fails
 * - api:rate_limited     → Emitted when API rate limit is hit
 * 
 * EVENTS LISTENED:
 * None - This is a service layer that doesn't listen to events
 * 
 * EVENT DATA STRUCTURE:
 * - api:request_started: { endpoint: string, method: string, data?: any }
 * - api:request_completed: { endpoint: string, method: string, response: any, duration: number }
 * - api:request_failed: { endpoint: string, method: string, error: Error, duration: number }
 * - api:rate_limited: { endpoint: string, retryAfter: number }
 */

import { API_ENDPOINTS, ERROR_CODES, DEFAULT_CONFIG } from '../constants/index.js';
import { TypeCheckers } from '../types/index.js';
import { Validation, Performance, Debug } from '../utils/index.js';

/**
 * Cart API Service
 * @class
 */
export class CartAPI {
  /**
   * @param {CartConfig} [config] - Configuration options
   */
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.retryCount = new Map();
    this._cartCache = null;
    this._pendingOperations = new Map();
    this._batchTimeout = null;
    this._batchUpdates = new Map();
  }

  /**
   * Get current cart state
   * @param {Object} [options] - Optional fetch options
   * @param {AbortSignal} [options.signal] - AbortSignal to cancel the request
   * @returns {Promise<Cart>} Cart object
   * @throws {Error} If cart fetch fails
   */
  async getCart(options = {}) {
    return Performance.measure(async () => {
      try {
        Debug.log('info', '[CartAPI] Fetching cart');
        const response = await this.fetchWithRetry(API_ENDPOINTS.CART, options);
        const cart = await response.json();
        
        Debug.log('info', '[CartAPI] Cart data received', {
          status: response.status,
          itemCount: cart.item_count,
          items: cart.items,
          total: cart.total_price
        });
        
        if (!Validation.validateCart(cart)) {
          Debug.log('error', '[CartAPI] Invalid cart data', cart);
          throw new Error('Invalid cart data received');
        }
        
        Debug.log('info', '[CartAPI] Cart fetched successfully', {
          itemCount: cart.item_count,
          items: cart.items.map(item => ({
            id: item.id,
            title: item.product_title,
            quantity: item.quantity
          }))
        });
        
        return cart;
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw error;
        }
        Debug.log('error', '[CartAPI] Failed to fetch cart', {
          error,
          stackTrace: error.stack
        });
        throw this.createError(ERROR_CODES.CART_ERROR, error.message);
      }
    }, 'getCart');
  }

  /**
   * Add item to cart
   * @param {number} variantId - Product variant ID
   * @param {number} [quantity=1] - Item quantity
   * @param {Object} [properties={}] - Custom properties
   * @returns {Promise<{item: CartItem, cart: Cart}>} Added item and updated cart state
   * @throws {Error} If add to cart fails
   */
  async addToCart(variantId, quantity = 1, properties = {}) {
    return Performance.measure(async () => {
      Debug.log('info', '[CartAPI] Adding item to cart', {
        variantId,
        quantity,
        properties
      });

      // Validate inputs
      Debug.log('info', '[CartAPI] Validating variant ID', {
        variantId,
        type: typeof variantId,
        isValid: Validation.validateVariantId(variantId)
      });
      
      if (!Validation.validateVariantId(variantId)) {
        throw this.createError(ERROR_CODES.INVALID_VARIANT, `Invalid variant ID: ${variantId} (type: ${typeof variantId})`);
      }
      
      if (!Validation.validateQuantity(quantity)) {
        throw this.createError(ERROR_CODES.INVALID_QUANTITY, 'Invalid quantity');
      }

      const payload = {
        id: variantId,
        quantity: quantity,
        properties: properties
      };

      try {
        // Add the item first
        Debug.log('info', '[CartAPI] Sending add to cart request', payload);
        
        const addResponse = await this.fetchWithRetry(API_ENDPOINTS.ADD, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!addResponse.ok) {
          const errorData = await addResponse.json().catch(() => ({}));
          Debug.log('error', '[CartAPI] Add to cart request failed', {
            status: addResponse.status,
            error: errorData
          });
          throw new Error(errorData.message || `HTTP ${addResponse.status}`);
        }

        const addedItem = await addResponse.json();
        
        Debug.log('info', '[CartAPI] Item added successfully', {
          item: addedItem
        });
        
        if (!Validation.validateCartItem(addedItem)) {
          Debug.log('error', '[CartAPI] Invalid item data', addedItem);
          throw new Error('Invalid cart item data received');
        }

        // Get updated cart state
        Debug.log('info', '[CartAPI] Fetching updated cart state');
        const cartResponse = await this.fetchWithRetry(API_ENDPOINTS.CART);
        const cart = await cartResponse.json();

        if (!Validation.validateCart(cart)) {
          Debug.log('error', '[CartAPI] Invalid cart data', cart);
          throw new Error('Invalid cart data received');
        }

        Debug.log('info', '[CartAPI] Add to cart completed', { 
          addedItem,
          newCartState: {
            itemCount: cart.item_count,
            items: cart.items.map(item => ({
              id: item.id,
              title: item.product_title,
              quantity: item.quantity
            }))
          }
        });
        
        return {
          item: addedItem,
          cart: cart
        };
      } catch (error) {
        Debug.log('error', '[CartAPI] Add to cart failed', {
          error,
          stackTrace: error.stack,
          payload
        });
        
        // Handle specific Shopify errors
        if (error.message.includes('sold out') || error.message.includes('out of stock')) {
          throw this.createError(ERROR_CODES.OUT_OF_STOCK, error.message);
        }
        
        throw this.createError(ERROR_CODES.CART_ERROR, error.message);
      }
    }, 'addToCart');
  }

  /**
   * Update cart items
   * @param {Object} updates - Updates object with variant IDs as keys and quantities as values
   * @returns {Promise<Cart>} Updated cart object
   * @throws {Error} If update fails
   */
  async updateCart(updates) {
    return Performance.measure(async () => {
      // Validate updates object
      for (const [variantId, quantity] of Object.entries(updates)) {
        if (!Validation.validateVariantId(Number(variantId))) {
          throw this.createError(ERROR_CODES.INVALID_VARIANT, `Invalid variant ID: ${variantId}`);
        }
        
        if (!Validation.validateQuantity(quantity)) {
          throw this.createError(ERROR_CODES.INVALID_QUANTITY, `Invalid quantity: ${quantity}`);
        }
      }

      try {
        const response = await this.fetchWithRetry(API_ENDPOINTS.UPDATE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const cart = await response.json();
        
        if (!Validation.validateCart(cart)) {
          throw new Error('Invalid cart data received');
        }

        Debug.log('debug', 'Cart updated successfully', { updates });
        return cart;
      } catch (error) {
        Debug.log('error', 'Failed to update cart', error);
        throw this.createError(ERROR_CODES.CART_ERROR, error.message);
      }
    }, 'updateCart');
  }

  /**
   * Change single item quantity with batching and optimistic updates
   * @param {number} variantId - Product variant ID
   * @param {number} quantity - New quantity
   * @returns {Promise<Cart>} Updated cart object
   */
  async changeItemQuantity(variantId, quantity) {
    return Performance.measure(async () => {
        if (!Validation.validateVariantId(variantId)) {
            throw this.createError(ERROR_CODES.INVALID_VARIANT, 'Invalid variant ID');
        }
        
        if (!Validation.validateQuantity(quantity)) {
            throw this.createError(ERROR_CODES.INVALID_QUANTITY, 'Invalid quantity');
        }

        try {
            // Check if there's already a pending operation for this variant
            const pendingPromise = this._pendingOperations.get(variantId);
            if (pendingPromise) {
                Debug.log('info', '[CartAPI] Waiting for pending operation', {
                    variantId,
                    quantity
                });
                await pendingPromise;
            }

            // Use cached cart if available and recent
            const cachedCart = this._cartCache?.cart;
            const isCacheValid = cachedCart && (Date.now() - this._cartCache.timestamp) < 2000;
            const currentCart = isCacheValid ? cachedCart : await this.getCart();
            
            const lineItem = currentCart.items.find(item => item.variant_id === variantId);
            if (!lineItem) {
                throw this.createError(ERROR_CODES.INVALID_VARIANT, 'Item not found in cart');
            }

            // Skip if quantity hasn't changed
            if (lineItem.quantity === quantity) {
                return currentCart;
            }

            // Prevent direct updates to linked items (items with _for property)
            // They should only be updated through their main product (with _with property)
            // Handle both object and array formats for properties
            let hasForProperty = false;
            let hasWithProperty = false;
            try {
              if (lineItem.properties) {
                if (Array.isArray(lineItem.properties)) {
                  hasForProperty = lineItem.properties.some(prop => prop && prop.name === '_for' && prop.value);
                  hasWithProperty = lineItem.properties.some(prop => prop && prop.name === '_with' && prop.value);
                } else if (typeof lineItem.properties === 'object') {
                  hasForProperty = !!lineItem.properties._for;
                  hasWithProperty = !!lineItem.properties._with;
                }
              }
            } catch (error) {
              Debug.log('warn', '[CartAPI] Error checking properties:', error);
            }
            
            if (hasForProperty && !hasWithProperty) {
                Debug.log('warn', '[CartAPI] Attempted to update linked item directly, ignoring', {
                    variantId,
                    quantity
                });
                return currentCart; // Return current cart without changes
            }

            // Check if this item has _with property (main product)
            // If so, find all linked items with matching _for property and update their quantities too
            // Handle both object and array formats for properties
            let withProperty = null;
            try {
              if (lineItem.properties) {
                if (Array.isArray(lineItem.properties)) {
                  const withProp = lineItem.properties.find(prop => prop && prop.name === '_with');
                  withProperty = withProp ? withProp.value : null;
                } else if (typeof lineItem.properties === 'object') {
                  withProperty = lineItem.properties._with || null;
                }
              }
            } catch (error) {
              Debug.log('warn', '[CartAPI] Error getting _with property:', error);
            }
            
            const linkedItemsToUpdate = new Map();
            
            if (withProperty) {
                Debug.log('info', '[CartAPI] Item has _with property, finding linked items', {
                    variantId,
                    withProperty,
                    quantity
                });
                
                // Find all items with matching _for property
                currentCart.items.forEach(item => {
                    let forProperty = null;
                    try {
                      if (item.properties) {
                        if (Array.isArray(item.properties)) {
                          const forProp = item.properties.find(prop => prop && prop.name === '_for');
                          forProperty = forProp ? forProp.value : null;
                        } else if (typeof item.properties === 'object') {
                          forProperty = item.properties._for || null;
                        }
                      }
                    } catch (error) {
                      Debug.log('warn', '[CartAPI] Error checking item _for property:', error);
                    }
                    
                    if (forProperty && forProperty === withProperty && item.variant_id !== variantId) {
                        linkedItemsToUpdate.set(item.variant_id, quantity);
                        Debug.log('info', '[CartAPI] Found linked item to update', {
                            linkedVariantId: item.variant_id,
                            currentQuantity: item.quantity,
                            newQuantity: quantity
                        });
                    }
                });
            }

            // Create optimistic update
            const optimisticCart = JSON.parse(JSON.stringify(currentCart));
            const optimisticItem = optimisticCart.items.find(item => item.variant_id === variantId);
            if (optimisticItem) {
                optimisticItem.quantity = quantity;
                optimisticCart.item_count = optimisticCart.items.reduce((sum, item) => sum + item.quantity, 0);
            }
            
            // Update optimistic quantities for linked items
            linkedItemsToUpdate.forEach((linkedQuantity, linkedVariantId) => {
                const linkedOptimisticItem = optimisticCart.items.find(item => item.variant_id === linkedVariantId);
                if (linkedOptimisticItem) {
                    linkedOptimisticItem.quantity = linkedQuantity;
                }
            });
            
            // Recalculate item count after all updates
            optimisticCart.item_count = optimisticCart.items.reduce((sum, item) => sum + item.quantity, 0);

            // Update cache with optimistic data
            this._cartCache = {
                cart: optimisticCart,
                timestamp: Date.now()
            };

            // Add to batch updates (main item + all linked items)
            this._batchUpdates.set(variantId, quantity);
            linkedItemsToUpdate.forEach((linkedQuantity, linkedVariantId) => {
                this._batchUpdates.set(linkedVariantId, linkedQuantity);
            });

            // Create a promise for this operation
            const operationPromise = new Promise((resolve, reject) => {
                // Clear any existing timeout
                if (this._batchTimeout) {
                    clearTimeout(this._batchTimeout);
                }

                // Set new timeout to process batch
                this._batchTimeout = setTimeout(async () => {
                    try {
                        const updates = Object.fromEntries(this._batchUpdates);
                        this._batchUpdates.clear();

                        const response = await this.fetchWithRetry(API_ENDPOINTS.UPDATE, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ updates })
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                        }

                        const cart = await response.json();
                        if (!Validation.validateCart(cart)) {
                            throw new Error('Invalid cart data received');
                        }

                        // Update cache with real data
                        this._cartCache = {
                            cart,
                            timestamp: Date.now()
                        };

                        // Resolve all pending promises
                        for (const [vid, promise] of this._pendingOperations) {
                            promise.resolve(cart);
                            this._pendingOperations.delete(vid);
                        }

                    } catch (error) {
                        // Reject all pending promises
                        for (const [vid, promise] of this._pendingOperations) {
                            promise.reject(error);
                            this._pendingOperations.delete(vid);
                        }
                    }
                }, 50); // 50ms batch window

                // Store the promise resolvers
                this._pendingOperations.set(variantId, { resolve, reject });
            });

            return operationPromise;

        } catch (error) {
            Debug.log('error', '[CartAPI] Failed to change item quantity', {
                error,
                variantId,
                quantity,
                stackTrace: error.stack
            });
            throw this.createError(ERROR_CODES.CART_ERROR, error.message);
        }
    }, 'changeItemQuantity');
}

  /**
   * Clear entire cart
   * @returns {Promise<Cart>} Empty cart object
   * @throws {Error} If clear fails
   */
  async clearCart() {
    return Performance.measure(async () => {
      try {
        const response = await this.fetchWithRetry(API_ENDPOINTS.CLEAR, {
          method: 'POST'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const cart = await response.json();
        
        if (!Validation.validateCart(cart)) {
          throw new Error('Invalid cart data received');
        }

        Debug.log('debug', 'Cart cleared successfully');
        return cart;
      } catch (error) {
        Debug.log('error', 'Failed to clear cart', error);
        throw this.createError(ERROR_CODES.CART_ERROR, error.message);
      }
    }, 'clearCart');
  }

  /**
   * Remove item from cart
   * @param {number} variantId - Product variant ID
   * @returns {Promise<Cart>} Updated cart object
   */
  async removeFromCart(variantId) {
    return this.changeItemQuantity(variantId, 0);
  }

  /**
   * Fetch with retry logic
   * @private
   * @param {string} url - Request URL
   * @param {Object} [options] - Fetch options
   * @returns {Promise<Response>} Fetch response
   */
  async fetchWithRetry(url, options = {}) {
    const requestId = `${options.method || 'GET'}_${url}_${Date.now()}`;
    let currentRetries = 0;
    const maxRetries = this.config.maxRetries;
    const timeout = this.config.apiTimeout;
    const externalSignal = options.signal;

    while (currentRetries <= maxRetries) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      if (externalSignal) {
        if (externalSignal.aborted) {
          clearTimeout(timeoutId);
          throw Object.assign(new Error('Aborted'), { name: 'AbortError' });
        }
        externalSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          controller.abort();
        }, { once: true });
      }
      const fetchOptions = {
        ...options,
        signal: controller.signal
      };

      try {
        Debug.log('info', `[CartAPI] Attempt ${currentRetries + 1}/${maxRetries + 1}`, {
          requestId,
          url,
          method: options.method || 'GET'
        });

        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        
        // Handle abort (timeout or external cancel)
        if (error.name === 'AbortError') {
          if (externalSignal?.aborted) {
            Debug.log('info', '[CartAPI] Request aborted by signal');
            throw Object.assign(new Error('Aborted'), { name: 'AbortError' });
          }
          Debug.log('warn', '[CartAPI] Request timeout', {
            requestId,
            attempt: currentRetries + 1,
            timeout: `${timeout}ms`
          });
          if (currentRetries === maxRetries) {
            throw this.createError(ERROR_CODES.TIMEOUT_ERROR, 'Request timeout');
          }
        }
        
        // Handle other errors
        else if (!this.isRetryableError(error) || currentRetries === maxRetries) {
          Debug.log('error', '[CartAPI] Request failed', {
            requestId,
            error: error.message,
            isRetryable: this.isRetryableError(error),
            attempts: currentRetries + 1
          });
          
          throw this.createError(
            this.isRetryableError(error) ? ERROR_CODES.NETWORK_ERROR : ERROR_CODES.API_ERROR,
            error.message
          );
        }
        
        // Prepare for retry
        currentRetries++;
        const delay = Math.min(Math.pow(2, currentRetries) * 1000, 8000); // Cap at 8 seconds
        
        Debug.log('warn', '[CartAPI] Retrying request', {
          requestId,
          attempt: currentRetries,
          delay: `${delay}ms`,
          error: error.message
        });
        
        await this.delay(delay);
      }
    }
  }

  /**
   * Check if error is retryable
   * @private
   * @param {Error} error - Error to check
   * @returns {boolean} Is retryable
   */
  isRetryableError(error) {
    const retryableErrors = [
      'Failed to fetch',
      'NetworkError',
      'TypeError'
    ];
    
    return retryableErrors.some(retryableError => 
      error.message.includes(retryableError)
    );
  }

  /**
   * Create standardized error
   * @private
   * @param {string} code - Error code
   * @param {string} message - Error message
   * @returns {Error} Standardized error
   */
  createError(code, message) {
    const error = new Error(message);
    error.code = code;
    error.timestamp = new Date().toISOString();
    return error;
  }

  /**
   * Delay execution
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get retry statistics
   * @returns {Object} Retry statistics
   */
  getRetryStats() {
    return {
      activeRetries: this.retryCount.size,
      retryDetails: Object.fromEntries(this.retryCount)
    };
  }

  /**
   * Reset retry counters
   */
  resetRetries() {
    this.retryCount.clear();
  }
} 